import { Alert, Button, Heading } from '@mm/components/src';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import { showInstaChampAlertSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import css from './InstagramStoryReaction.module.css';
import { InactiveChannelAlertSmart } from '../../campaigns/panels/DripCampaignDetailsPanel/InactiveChannelAlert';
import { EditStartBotHeader } from '../common/StartBotPromoterPanel/EditStartBotHeader/EditStartBotHeader';
import { useEditPagePrimeState } from '../../../common/hooks/use-edit-page-prime-state';
import {
  PathGraphContext,
  useNewGraphInstance,
} from '../../../../common/widgets/common/PathGraph/PathGraph';
import { usePromoterActivator } from '../common/use-promoter-activator';
import { StartBotsDialogueEditor } from '../common/StartBotsDialogueEditor/StartBotsDialogueEditor';
import { BotBuilderRoutes } from '../../../../routes/bot-builder-routes';
import { DialogueSelectorWithReload } from '../common/DialogueSelectorWithReload/DialogueSelectorWithReload';
import { useSupportedWidgets } from '../../../../common/components/AddWidgetInsert/AddWidgetInsert';
import { IG_SUPPORTED_WIDGET_TYPES } from '../instagramStoryMention/InstagramStoryMentionEditor';
import Spinner from '../../../../common/components/Spinner';
import { DialoguePreview } from '../../../../modules/DailoguePreview/DialoguePreview';
import { InstagramRespondRadioBtns } from '../../common/components/IGRespondRadioBtns/InstagramRespondRadioBtns';
import { useResolutionInfo } from '@mm/hooks';

type Props = {
  className?: string;
};

export const InstagramStoryReaction = ({ className }: Props) => {
  const { widgets, page, promoter } = useEditPagePrimeState(
    'instagram_story_reaction',
  );
  const [promoters, setPromoters] = useState<PromoterModel[]>([]);
  const { activatePromoter } = usePromoterActivator(promoter);
  const chatbot = useSelector(activeChatbotSelector);
  const pathGraph = useNewGraphInstance(promoter?.pageId);
  const { allWidgets: allSupportedWidgets } = useSupportedWidgets();
  const { screenWidth } = useResolutionInfo();
  const widgetsToExclude = allSupportedWidgets
    .filter(w => !IG_SUPPORTED_WIDGET_TYPES.includes(w.type))
    .map(w => w.type);
  const [showInstaChampAlert, instagramUsername] = useSelector(
    showInstaChampAlertSelector,
  );
  const promoterCRUDActions = usePromoterCRUDActions();

  useEffect(() => {
    const fetchPromoters = async () => {
      const response = await promoterCRUDActions.retrieveAll(
        chatbot.id,
        'instagram_story_reaction',
      );
      setPromoters(response);
    };
    fetchPromoters();
  }, []);

  const instachampEditUrl = `/instachamp/@${instagramUsername}/story_reactions/${promoter?.id}`;

  const dialogueSelectorDropdown = useMemo(() => {
    const promoterDialogueUpdate = async (dialogueId: number) => {
      if (promoter && promoter.id) {
        const updatedPromoter = {
          ...promoter,
          pageId: dialogueId,
        };
        const savedPromoter = await promoterCRUDActions.update(
          chatbot?.id,
          updatedPromoter,
        );

        const newDialogueId = savedPromoter?.pageId;

        if (newDialogueId) {
          const newUrl = BotBuilderRoutes.instagramStoryReactionEdit.getLink({
            chatbotId: chatbot?.id,
            dialogueId: newDialogueId,
            promoterId: promoter.id,
          });
          return {
            newDialogueId,
            newUrl,
          };
        }
      }
    };

    return promoter?.pageId ? (
      <DialogueSelectorWithReload
        dialogueId={promoter.pageId}
        entityUpdateFunction={promoterDialogueUpdate}
      />
    ) : null;
  }, [promoter, chatbot?.id]);

  if (!promoter?.id) {
    return (
      <div className={classNames(className, css.container)}>
        <Spinner standalone size={24} />
      </div>
    );
  }

  return (
    <div className={classNames(className, css.container)}>
      <InactiveChannelAlertSmart instagram type="Story Reaction" />

      {page && (
        <EditStartBotHeader
          promoter={promoter}
          page={page}
          botIsTemplate={!!chatbot.template}
          customActivateAction={activatePromoter}
        />
      )}
      {showInstaChampAlert && (
        <Alert type="warning" showCloseIcon={false}>
          <div>
            This Instagram account was connected via InstaChamp. Making changes
            here may block you from making further changes in InstaChamp.
          </div>
          <Button
            onClick={() => {
              window.location.href = instachampEditUrl;
            }}
          >
            Edit with InstaChamp
          </Button>
        </Alert>
      )}
      <div className={css.editor_container}>
        <div className={css.editor_block}>
          <Heading size={5} text="Settings" className={css.title} />
          <InstagramRespondRadioBtns
            promoter={promoter}
            promoters={promoters}
            type="mentions"
            customKeywordBroadPosition={screenWidth > 600 ? 'right' : 'bottom'}
            customKeywordBroadAlign="center"
          />
        </div>
        <div className={css.content}>
          <div className={css.dialogue_editor_container}>
            <PathGraphContext.Provider value={pathGraph}>
              <Heading size={3} className={css.heading}>
                Choose or create a Dialogue
              </Heading>

              {dialogueSelectorDropdown}
              <StartBotsDialogueEditor
                chatbotId={chatbot.id}
                widgets={widgets}
                widgetsToExclude={widgetsToExclude}
                dialogueId={promoter.pageId}
                platforms={['instagram']}
                showAttributesStartWarning
                forInstagram
              />
            </PathGraphContext.Provider>
          </div>
        </div>
        {promoter.pageId && (
          <div className={css.preview}>
            <DialoguePreview
              platforms={['instagram']}
              dialogueId={promoter?.pageId}
              style={{ top: -20, marginTop: 20 }}
              sticky
            />
          </div>
        )}
      </div>
    </div>
  );
};
